/**
 * @file  src\pages\Home.js
 * @brief  This file is responsible for home page.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, createContext } from "react";
import _ from "lodash";
import Form from "react-bootstrap/Form";
import PageTransition from "../../components/Common/PageTransition";
import LeftpanelTab from "../../components/Onboarding/Dashboard/LeftpanelTab";
import SiteFooter from "../../components/Onboarding/SiteFooter";
import WantAdCard from "../../components/responsive/WantAdCard/WantAdCard";
import PropertyListing from "../../components/Onboarding/Dashboard/v2/PropertyListing";
import useBreakpoint from "../../hooks/useBreakpoint";
import Offcanvas from "react-bootstrap/Offcanvas";
import SelectDropDown from "../../components/Common/SelectDropDown";
import MatchTutorial from "../../components/Tutorial/MatchTutorialModal";
import {
  showAgentPreferenceModal,
} from "../../reducers/ui";
import {
  BUY,
  WANT_AD_KEY,
  WANT_AD_PAGE_LIMIT,
  USER_INFO_KEY,
  HOME_PAGE,
  INVENTORY_KEY,
  STRENGTHEN_WANT_AD_KEY,
  STRENGTHEN_INVENTORY_KEY,
  USER_LOCATION_KEY,
  CLIENT_DETAILS_KEY,
  LONG_RENT,
  USER_TYPE_AGENT,
  USER_TYPE_MEMBER,
  DEFAULT_MAX_BUDGET_BUY,
  DEFAULT_MAX_BUDGET_LTR,
  DEFAULT_MAX_BUDGET_STR,
} from "../../constants/common";
import { getWantAds, getWantAdsPublic } from "../../services/wantAdService";
import { getMatches } from "@/services";
import HomeLoadingSkeleton from "../HomeLoadingSkeleton";
import {
  getLocalStorage,
  setLocalStorage,
  loggedInUser,
  removeMultipleLocalStorageItems,
  removeLocalStorage,
  checkTutorialDisplayStatus,
  getUserType,
  getTitleText,
} from "../../helpers/common";
import { useDispatch, useSelector } from "react-redux";
import {
  favouriteWantAd,
  unFavouriteWantAd,
} from "../../services/wantAdService";
import { toast } from "react-toastify";
import {
  getAgentsList,
  getUserInventoryCount,
} from "../../services/wantAdService";
import { handleTextBoxChange } from "../../actions/common";
import { pageURLs } from "@/constants";
import { getUserId } from "@/helpers";
import { useNavigate, useSearchParams } from "react-router-dom";
import GeoLocation from "../../components/Common/GeoLocation";
import { usePrevious } from "../../hooks/usePrevious";
import { saveTutorialStatus } from "../../services/commonServices";
import { checkGitchaAvailable, getGitchaAvailableStates, getGeoLocation } from "../../services/commonServices";
import GitchaAvailableLocations from "../../components/GitchaAvailableLocationsScreen";
import { isMlsRestrictedTenant } from "../../helpers/mlsCityFilter";
import { fetchGitchaAvailableCities } from "../../actions/locations";
import RequestStayAvailability from "../../components/Common/RequestStayAvailability";
import AddPropertyPlanChecking from "../../components/Common/AddPropertyPlanChecking";
import SearchLocationScreen from "../../components/SearchLocationScreen";
import HeroSplash from "../../components/HeroSplash/HeroSplash";
import TenantLoginPage from "../../components/TenantLoginPage/TenantLoginPage";
import DemoTransition from "../../components/DemoTransition/DemoTransition";
import TourLaunchDialog from "../../components/EnterpriseTour/TourLaunchDialog";
import { useEnterpriseTour } from "../../components/EnterpriseTour/EnterpriseTourContext";
import { TOUR_COMPLETED_KEY } from "../../components/EnterpriseTour/constants";
import { useAppConfig } from "../../appConfig";
import SearchTransition from "../../components/HeroSplash/SearchTransition";
import PreLaunchCityRequestModal from "../../components/Common/PreLauchCityRequestModal";
import WelcomeModal from "../../components/Onboarding/WelcomeModal";
import AgentLiketoStartModal from "@/components/Onboarding/UserRegister/AgentLiketoStartModal";
import { PageWrapper } from "@/components/PageWrapper";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import useTenantLocations from "../../hooks/useTenantLocations";
import GitchaNotAvailableModal from "@/components/Common/GitchaNotAvailableModal";
import BellIcon from "../../images/bell.svg";
import { Search } from "lucide-react";

export const filterContext = createContext();

const Home = () => {

  const [searchParams] = useSearchParams();
  const AdTypeFromUrl = searchParams.get("AdType");
  const showAgentModal = searchParams.get("showAgentModal");
  const tutorialPage = 1;
  const myRef = useRef(null);
  const navigate = useNavigate();
  const browserLocation = getLocalStorage("userLocation");
  const { isMobile } = useBreakpoint();
  // Use original PropertyListing on desktop, WantAdCard on mobile
  const CardComponent = isMobile ? WantAdCard : PropertyListing;

  // Feed cache restoration - read cached state from sessionStorage (set before navigating to detail)
  const FEED_CACHE_KEY = "feed_cache";
  let feedCache = null;
  try {
    const feedCacheRaw = sessionStorage.getItem(FEED_CACHE_KEY);
    feedCache = feedCacheRaw ? JSON.parse(feedCacheRaw) : null;
  } catch (e) {
    sessionStorage.removeItem(FEED_CACHE_KEY);
  }
  // 60-second TTL (was 10 min). The longer window made stale match data
  // visible after a recompute - prospects would re-enter the page and see
  // pre-recompute scores at the top of the list (45/45/70 instead of the
  // current 90/89/89). Brief same-session navigations still benefit from
  // the cache (skipping a 200-300ms refetch flash) but anything older than
  // a minute re-fetches fresh.
  const isCacheValid = feedCache && (Date.now() - feedCache.timestamp) < 60 * 1000;
  const restoringFromCache = useRef(isCacheValid);

  const [isMatch, setIsMatch] = useState(false);

  const dispatch = useDispatch();

  // MLS city restriction - pre-fetch available cities for BLS tenants
  const mlsRestricted = isMlsRestrictedTenant();
  const availableCities = useSelector(
    (state) => state.locations.availableCities,
  );

  // [US-001] Default location from tenant config (not from availableCities which has wrong shape)
  useEffect(() => {
    if (mlsRestricted && availableCities.length === 0) {
      getGitchaAvailableStates()
        .then((response) => {
          const states = _.get(response, "result", []);
          states.forEach((item) => {
            dispatch(fetchGitchaAvailableCities({ state: item.StateCode }));
          });
        })
        .catch((err) => console.log("Failed to fetch available states:", err));
    }
  }, [mlsRestricted]);

  const { isGitchaDefaultWebsite } = useCurrentWebsite()
  const appConfig = useAppConfig();
  const enterpriseTour = useEnterpriseTour();

  // Demo transition state - cinematic overlay before reload
  const [showDemoTransition, setShowDemoTransition] = useState(false);

  // Login pending state - keeps login page visible until demo transition covers screen
  const [loginPending, setLoginPending] = useState(false);

  // Enterprise tour launch dialog - show as soon as appConfig loads for demo tenants
  const [showTourLaunch, setShowTourLaunch] = useState(false);


  // BLS tenant login success - auto-populate default market and show transition
  const handleTenantLoginSuccess = () => {
    const defaultMarket = appConfig?.tenant?.defaultMarket;
    if (defaultMarket) {
      const locationObj = {
        latitude: defaultMarket.latitude,
        longitude: defaultMarket.longitude,
        city: defaultMarket.city,
        state: defaultMarket.state,
        formatted_address: `${defaultMarket.city}, ${defaultMarket.state}`,
        isFromAvailableCities: true,
      };
      setLocalStorage("userLocation", locationObj);
    }
    // Tag Clarity session with prospect info for recording attribution
    const prospect = getLocalStorage("demoProspect");
    if (prospect && window.clarity) {
      window.clarity("identify", prospect.email);
      window.clarity("set", "organization", prospect.organization || "");
      window.clarity("set", "prospectName", prospect.firstName + " " + prospect.lastName);
    }

    // Keep login page visible until the demo transition overlay covers the screen
    setLoginPending(true);
    // Show cinematic transition for demo tenants, then reload
    if (appConfig?.tenant?.demoMode) {
      setShowDemoTransition(true);
    } else {
      window.location.reload();
    }
  };

  const handleDemoTransitionComplete = useCallback(() => {
    window.location.reload();
  }, []);

  const buyerOptions = [
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
    { value: "highPrice", label: "Highest price" },
    { value: "lowPrice", label: "Lowest price" },
  ];
  const matchOptions = [
    { value: "bestMatch", label: "Best Match" },
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
  ];
  const [showMatchTutorial, setMatchTutorial] = useState(false);
  const [currPage, setCurrPage] = useState(isCacheValid ? feedCache.currPage : 1);
  const [adList, setAdList] = useState(isCacheValid ? feedCache.adList : []);
  const [adListGitchaGenie, setAdListgitchaGenie] = useState(isCacheValid ? feedCache.adListGitchaGenie : []);
  const [adType, setAdType] = useState(
    AdTypeFromUrl
      ? AdTypeFromUrl
      : isCacheValid ? feedCache.adType : BUY,
  );
  const filterTabKey = getLocalStorage("FilterTabKey");
  const previousBudgetMin = getLocalStorage("priceRangeMatch");
  const [totalCount, setTotalCount] = useState(isCacheValid ? feedCache.totalCount : null);
  const [totalCountMatch, setTotalCountMatch] = useState(isCacheValid ? feedCache.totalCountMatch : null);
  // Slider price filter for Match tab - when set, filters displayed matches client-side
  const [matchPriceFilter, setMatchPriceFilter] = useState(null);
  const user = loggedInUser();
  const userType = getUserType();
  const [sessionData, setSessionData] = useState(
    getLocalStorage(USER_INFO_KEY),
  );

  // Show enterprise tour launch dialog only after feed data has loaded
  const feedLoadedRef = useRef(false);
  useEffect(() => {
    if (adList && adList.length > 0) {
      feedLoadedRef.current = true;
    }
  }, [adList]);

  useEffect(() => {
    if (
      appConfig?.tenant?.demoMode &&
      appConfig?.tenant?.demoTour &&
      user &&
      !enterpriseTour.isActive &&
      !localStorage.getItem(TOUR_COMPLETED_KEY) &&
      feedLoadedRef.current
    ) {
      // Feed is loaded - show after a short visual settle delay
      const timer = setTimeout(() => setShowTourLaunch(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [user, appConfig, enterpriseTour.isActive, adList]);

  const [results, setResults] = useState([]);
  //form States on  agent client search
  const [formData, setFormData] = useState("");
  const [reloadAgents, setReloadAgents] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [scrollDirection, setScrollDirection] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showNoResult, setShowNoResult] = useState(false);
  const [offCanvaShow, setOffCanvaShow] = useState(false);
  const handleCanvasClose = () => setOffCanvaShow(false);
  const handleCanvasShow = () => setOffCanvaShow(true);
  const savedUserLocation = getLocalStorage("userLocation");
  // For logged-in users, always trust the saved location - they already
  // validated it in a previous session. Set the flag so all downstream
  // checks (renderResult, clearFilterQuery, etc.) work correctly.
  if (user && savedUserLocation && !savedUserLocation.isFromAvailableCities) {
    savedUserLocation.isFromAvailableCities = true;
    setLocalStorage("userLocation", savedUserLocation);
  }
  const [userLocation, setUserLocation] = useState(() => {
    if (isCacheValid) return feedCache.userLocation;
    if (savedUserLocation?.isFromAvailableCities) return savedUserLocation;
    // For MLS-restricted tenants, eagerly default from tenant config so the
    // initial filterQuery already has lat/long and the feed fetches immediately.
    if (mlsRestricted) {
      const dm = window.__TENANT_CONFIG__?.defaultMarket;
      if (dm?.latitude && dm?.longitude) {
        const loc = {
          latitude: dm.latitude,
          longitude: dm.longitude,
          city: dm.city,
          state: dm.state,
          formatted_address: `${dm.city}, ${dm.state}`,
          isFromAvailableCities: true,
        };
        setLocalStorage("userLocation", loc);
        return loc;
      }
    }
    return null;
  });


  const [isGitchaGenieFilter, setIsGitchaGenieFilter] = useState(
    isCacheValid ? feedCache.isGitchaGenieFilter : (filterTabKey === "match" ? true : false),
  );
  const options = isGitchaGenieFilter ? matchOptions : buyerOptions;
  // Ref keeps the latest value so async callbacks never read a stale closure
  const isGitchaGenieFilterRef = useRef(isGitchaGenieFilter);
  useEffect(() => { isGitchaGenieFilterRef.current = isGitchaGenieFilter; }, [isGitchaGenieFilter]);

  // Ref tracking feed state for unmount cache save (avoids stale closures)
  const feedStateRef = useRef(null);
  const matchRequestIdRef = useRef(0);
  // If user navigated here from inventory with a specific property, don't restore cached selection
  const navSelectedProperty = getLocalStorage("selectedPropertyId");
  const cachePropertyMatches = !navSelectedProperty?.Inv_Id ||
    navSelectedProperty.Inv_Id === feedCache?.selectedProperty?.Inv_Id;
  const [selectedProperty, setSelectedProperty] = useState(
    isCacheValid && cachePropertyMatches ? feedCache.selectedProperty : null
  );
  const [reload, setReload] = useState(false);
  const [sortParam, setSortParam] = useState(isCacheValid ? feedCache.sortParam : options[0]);
  const [wantAdClickCounter, setWantAdClickCounter] = useState(0);
  const [gitchaNotAvailble, setGitchaNotAvailable] = useState(
    userLocation?.isFromAvailableCities ? true : false,
  );
  const wantAdCount = adList?.length;
  const wantAdCountGitchaGenie = adListGitchaGenie?.length;
  const locationString = getLocalStorage("LocationString");
  const [forceShowSplash, setForceShowSplash] = useState(false);
  const [isCityInPreLaunch, setIsCityInPreLaunch] = useState(false);
  const [PreLaunchStateName, setPreLaunchStateName] = useState(null);
  const [showGitchaGeineLabel, setShowGitchaGeineLabel] = useState(isCacheValid ? feedCache.showGitchaGeineLabel : false);

  // Hero splash transition state
  const [showTransition, setShowTransition] = useState(false);
  const [transitionCity, setTransitionCity] = useState("");

  const [showRequestStayPopuop, setShowRequestStayPopup] = useState(false);
  const [showGitchaNotAvailablePopup, setShowGitchaNotAvailablePopup] = useState(false);
  const handleStayRequestPopupClose = () => {
    setShowRequestStayPopup(false);
  };
  /**
   * To handle user's default location when location permission is enabled
   * @param {*} location
   */
  const handleUserLocation = async (location) => {
    // Skip GeoLocation callback when restoring cached state
    if (restoringFromCache.current) return;
    // Skip if user already has a validated location (e.g. refreshing
    // the feed page - don't let browser geolocation overwrite it)
    const existing = getLocalStorage("userLocation");
    if (existing?.isFromAvailableCities) return;

    setLocalStorage("userLocation", location);
    if (location) {

      const reqPayLoad = {
        county: location.county,
        state: location.state,
      };
      await checkGitchaAvailable(reqPayLoad).then((data) => {
        if (_.get(data, "result") == true) {
          location["isFromAvailableCities"] = true;
          // Re-save with the flag so subsequent page loads skip the "limited areas" screen
          setLocalStorage("userLocation", location);
          setGitchaNotAvailable(false);
          if (!locationString || locationString == "") {
            setUserLocation(location);
          }
        } else {
          if (!locationString || locationString == "") {
            setUserLocation(null);
          }
          setGitchaNotAvailable(true);
        }
      }).catch((err) => {
        // On error, fall through to Available Locations screen
        if (!locationString || locationString == "") {
          setUserLocation(null);
        }
        setGitchaNotAvailable(true);
      });
    } else {
      setUserLocation(null);
    }

  }

  // [US-001] Auto-default location for restricted tenants using tenant config directly
  // Uses appConfig.tenant.defaultMarket (has lat/lng/city/state) - no geocoding needed
  useEffect(() => {
    if (!mlsRestricted || userLocation) return;
    // Skip if user already has a saved location
    const saved = getLocalStorage("userLocation");
    if (saved?.isFromAvailableCities) return;

    const defaultMarket = appConfig?.tenant?.defaultMarket;
    if (!defaultMarket) return;

    const locationObj = {
      latitude: defaultMarket.latitude,
      longitude: defaultMarket.longitude,
      city: defaultMarket.city,
      state: defaultMarket.state,
      formatted_address: `${defaultMarket.city}, ${defaultMarket.state}`,
      isFromAvailableCities: true,
    };
    setLocalStorage("userLocation", locationObj);
    setUserLocation(locationObj);
    setGitchaNotAvailable(false);
  }, [mlsRestricted, appConfig?.tenant?.defaultMarket]);

  let showNotification = checkTutorialDisplayStatus(tutorialPage);
  const [showNotificationIcon, setNotificationIcon] = useState(false);
  const PRICE_MIN = 0;
  const PRICE_MAX = null;
  const SQFT_MIN = null;
  const SQFT_MAX = null;
  const initialQuery =
    adType == "STR"
      ? {
        adType: adType,
        propertyType: [],
        alwpets: null,
        adaAccess: null,
        pvtBath: null,
        pvtEntrance: null,
        typeOfPlace: [10, 11, 6],
        priceMin: PRICE_MIN,
        priceMax: PRICE_MAX,
        sortType: sortParam?.value,
        bedrooms: "",
        beds: "",
        baths: "",
        sqrftMin: SQFT_MIN,
        sqrftMax: SQFT_MAX,
        lat: _.get(userLocation, "latitude", ""),
        long: _.get(userLocation, "longitude", ""),
        city: _.get(userLocation, "city", ""),
        state: _.get(userLocation, "state", ""),
        search: "",
        page: currPage,
        limit: WANT_AD_PAGE_LIMIT,
        fromDate: null,
        toDate: null,
        agentOnly: false,
      }
      : adType == "LTR"
        ? {
          adType: adType,
          propertyType: [],
          typeOfPlace: [10, 11, 6],
          priceMin: PRICE_MIN,
          priceMax: PRICE_MAX,
          sortType: sortParam?.value,
          bedrooms: "",
          baths: "",
          sqrftMin: SQFT_MIN,
          sqrftMax: SQFT_MAX,
          search: "",
          lat: _.get(userLocation, "latitude", ""),
          long: _.get(userLocation, "longitude", ""),
          city: _.get(userLocation, "city", ""),
          state: _.get(userLocation, "state", ""),
          page: currPage,
          limit: WANT_AD_PAGE_LIMIT,
          agentOnly: false,
        }
        : {
          adType: adType,
          propertyType: [],
          priceMin: PRICE_MIN,
          priceMax: PRICE_MAX,
          sortType: sortParam?.value,
          bedrooms: "",
          baths: "",
          sqrftMin: SQFT_MIN,
          sqrftMax: SQFT_MAX,
          lat: _.get(userLocation, "latitude", ""),
          long: _.get(userLocation, "longitude", ""),
          city: _.get(userLocation, "city", ""),
          state: _.get(userLocation, "state", ""),
          search: "",
          page: currPage,
          limit: WANT_AD_PAGE_LIMIT,
          agentOnly: false,
        };

  const initialQueryGitchaGenie = {
    inventoryId: selectedProperty?.Inv_Id,
    adType: adType,
    city: selectedProperty?.City,
    state: selectedProperty?.State,
    priceMin: PRICE_MIN,
    priceMax: PRICE_MAX,
    sortType: sortParam?.value,
    page: currPage,
    limit: WANT_AD_PAGE_LIMIT,
  };

  const [filterQuery, setFilterQuery] = useState(isCacheValid ? feedCache.filterQuery : initialQuery);
  const [filterQueryGitchaGenie, setFilterQueryGitchaGenie] = useState(
    isCacheValid ? feedCache.filterQueryGitchaGenie : initialQueryGitchaGenie,
  );

  let DEFAULT_MAX_BUDGET;
  if (adType === BUY) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_BUY;
  } else if (adType === LONG_RENT) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_LTR;
  } else {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_STR;
  }

  const priceMinGitchaGenieFromStorage = previousBudgetMin
    ? previousBudgetMin[0]
    : null;
  const priceMaxGitchaGenieFromStorage = previousBudgetMin
    ? previousBudgetMin[1] == DEFAULT_MAX_BUDGET
      ? ""
      : previousBudgetMin[1]
    : null;

  const previousUserLocation = usePrevious(userLocation);

  // Persist userLocation to localStorage on every change AND before page
  // unloads (refresh/close). Belt-and-suspenders: the ref ensures the
  // beforeunload handler always has the latest value.
  const userLocationRef = useRef(userLocation);
  useEffect(() => {
    userLocationRef.current = userLocation;
    if (userLocation) {
      const toSave = { ...userLocation, isFromAvailableCities: true };
      localStorage.setItem("userLocation", JSON.stringify(toSave));
    }
  }, [userLocation]);
  useEffect(() => {
    const saveBeforeUnload = () => {
      const loc = userLocationRef.current;
      if (loc) {
        localStorage.setItem("userLocation", JSON.stringify({ ...loc, isFromAvailableCities: true }));
      }
    };
    window.addEventListener("beforeunload", saveBeforeUnload);
    return () => window.removeEventListener("beforeunload", saveBeforeUnload);
  }, []);

  useEffect(() => {
    if (showAgentModal) dispatch(showAgentPreferenceModal());
  }, [showAgentModal, dispatch]);


  useEffect(() => {
    if (restoringFromCache.current) return;
    if (
      !isGitchaGenieFilter &&
      !_.isEqual(previousUserLocation, userLocation)
    ) {
      updateQuery({
        lat: _.get(userLocation, "latitude", null),
        long: _.get(userLocation, "longitude", null),
        city: _.get(userLocation, "city", null),
        state: _.get(userLocation, "state", null),
      });
      setCurrPage(1);
      setAdList([]);
    }
  }, [userLocation]);

  useEffect(() => {
    if (restoringFromCache.current) return;
    // Fetch Buyers feed when filterQuery changes or when switching back to Buyers tab.
    // isGitchaGenieFilter in deps ensures the fetch fires when user clicks Buyers tab
    // after the page loaded on Match tab (where the fetch was skipped).
    if (!isGitchaGenieFilter && user) {
      fetchWantAds(filterQuery);
    } else {
    }
  }, [filterQuery, isGitchaGenieFilter]);

  useEffect(() => {
    if (restoringFromCache.current) return;
    if (!user && !isGitchaGenieFilter) {
      fetchWantAdsPublic(filterQuery);
    }
  }, [filterQuery, isGitchaGenieFilter]);

  // When Match tab activates and selectedProperty already has a value (loaded
  // while on Buyers tab), sync its inventoryId into filterQueryGitchaGenie.
  // updateFilterGitchaGenie is blocked by the isGitchaGenieFilterRef guard when
  // on Buyers tab, so this catch-up is needed.
  useEffect(() => {
    if (restoringFromCache.current) return;
    if (isGitchaGenieFilter && selectedProperty?.Inv_Id) {
      updateQueryGitchaGenie({
        inventoryId: selectedProperty.Inv_Id,
        city: selectedProperty.City,
        state: selectedProperty.State,
      });
    }
  }, [isGitchaGenieFilter]);

  useEffect(() => {
    if (restoringFromCache.current) return;
    if (!isGitchaGenieFilter) return;
    const nextInvId = selectedProperty?.Inv_Id || null;
    if (nextInvId === filterQueryGitchaGenie.inventoryId) return;
    updateQueryGitchaGenie({
      inventoryId: nextInvId,
      city: selectedProperty?.City || null,
      state: selectedProperty?.State || null,
    });
  }, [selectedProperty?.Inv_Id]);

  useEffect(() => {
    if (restoringFromCache.current) return;
    if (isGitchaGenieFilter) {
      fetchWantAdsMatchingGitchaGenie(filterQueryGitchaGenie);
    }
  }, [
    isGitchaGenieFilter,
    filterQueryGitchaGenie.inventoryId,
    filterQueryGitchaGenie.priceMin,
    filterQueryGitchaGenie.sortType,
    filterQueryGitchaGenie.priceMax,
    filterQueryGitchaGenie.page,
    filterQueryGitchaGenie.adType,
    reload == true,
  ]);

  useEffect(() => {
    if (restoringFromCache.current) return;
    updateQuery(initialQuery);
    setCurrPage(1);
    setAdList([]);
    let showNotification = checkTutorialDisplayStatus(tutorialPage);
  }, [sessionData?.userInfo?.userId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const fresh = getLocalStorage(USER_INFO_KEY);
      // Only update if userId actually changed (avoids resetting feed for same user)
      setSessionData(prev => {
        const prevId = prev?.userInfo?.userId;
        const freshId = fresh?.userInfo?.userId;
        return prevId !== freshId ? fresh : prev;
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, [user?.token?.accessToken]);

  useEffect(() => {
    if (restoringFromCache.current) return;
    if (isGitchaGenieFilter) {
      updateQueryGitchaGenie({ page: currPage });
    } else {
      updateQuery({ page: currPage });
    }
  }, [currPage]);

  useEffect(() => {
    if (restoringFromCache.current) return;
    if (isGitchaGenieFilter) {
      updateQueryGitchaGenie({ sortType: sortParam?.value });
    } else {
      updateQuery({ sortType: sortParam?.value });
    }
    setCurrPage(1);
    setAdList([]);
    setAdListgitchaGenie([]);
  }, [sortParam]);

  useEffect(() => {
    if (restoringFromCache.current) return;
    updateQueryGitchaGenie({
      inventoryId: selectedProperty?.Inv_Id,
      adType: adType,
      priceMin: priceMinGitchaGenieFromStorage || PRICE_MIN,
      priceMax: priceMaxGitchaGenieFromStorage || PRICE_MAX,
      city: selectedProperty?.City,
      state: selectedProperty?.State,
      sortType: sortParam?.value,
      page: currPage,
      limit: WANT_AD_PAGE_LIMIT,
    });
    updateQuery(initialQuery);
    setCurrPage(1);
    setAdList([]);
    setAdListgitchaGenie([]);
  }, [adType]);

  const clearFilterQuery = () => {
    sessionStorage.removeItem(FEED_CACHE_KEY);
    if (!browserLocation?.isFromAvailableCities) {
      setUserLocation(null);
      setAdList([]);
      initialQuery["lat"] = null;
      initialQuery["long"] = null;
      initialQuery["city"] = null;
      initialQuery["state"] = null;
    } else {
      setUserLocation(browserLocation);
    }
    setCurrPage(1);
    setAdType(BUY);
    setFilterQuery(initialQuery);
  };

  const clearFilterGitchaGenie = () => {
    setFilterQueryGitchaGenie(initialQueryGitchaGenie);
  };

  /**
   * function to call Ad list
   * @param {*} query
   */
  const fetchWantAds = async (query) => {

    const isDemoMode = appConfig?.tenant?.demoMode;
    const wantAdsQuery = {
      ...query,
      shouldHideNonSyndicatedAds: isGitchaDefaultWebsite,
    }

    if (_.get(query, "lat", "") && _.get(query, "long", "")) {
      setShowNoResult(false);
      await getWantAds(wantAdsQuery)
        .then((response) => {
          if (response.errorCode === 0) {
            const isPreLauch = _.get(response, "result.isPrelaunch", null);
            const StateName = _.get(response, "result.StateName", null);
            if (isPreLauch) {
              setIsCityInPreLaunch(true);
              setPreLaunchStateName(StateName);
            } else {
              const responseResult = _.get(response, "result.data", null);
              setTotalCount(_.get(response, "result.totalCount", "0"));
              const incomingPage = parseInt(_.get(query, "page", 1), 10);
              if (responseResult && responseResult.length > 0) {
                if (incomingPage === 1) {
                  setAdList(responseResult);
                } else {
                  setAdList(prev => [...prev, ...responseResult]);
                }
              } else if (
                responseResult &&
                responseResult.length === 0 &&
                incomingPage === 1
              ) {
                setShowNoResult(true);
                setAdList([]);
              }
            }
          }
        })
        .catch((err) => {
          console.error("fetchWantAds error:", err?.message || err);
        });
    } else {
      // No lat/long in query - skip fetch
    }
  };

  const fetchWantAdsPublic = async (query) => {
    if (_.get(query, "lat", "") && _.get(query, "long", "")) {
      setShowNoResult(false);
      await getWantAdsPublic(query)
        .then((response) => {
          if (response.errorCode === 0) {
            const isPreLauch = _.get(response, "result.isPrelaunch", null);
            const StateName = _.get(response, "result.StateName", null);
            if (isPreLauch) {
              setIsCityInPreLaunch(true);
              setPreLaunchStateName(StateName);
            } else {
              const responseResult = _.get(response, "result.data", null);
              setTotalCount(_.get(response, "result.totalCount", "0"));
              const incomingPage = parseInt(_.get(query, "page", 1), 10);
              if (responseResult && responseResult.length > 0) {
                if (incomingPage === 1) {
                  setAdList(responseResult);
                } else {
                  setAdList(prev => [...prev, ...responseResult]);
                }
              } else if (
                responseResult &&
                responseResult.length === 0 &&
                incomingPage === 1
              ) {
                setShowNoResult(true);
                setAdList([]);
              }
            }
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  /**
 * To fetch match want ads
 * @param {*} query
 */
  const fetchWantAdsMatchingGitchaGenie = async (query, options = {}) => {
    if (!query.inventoryId) {
      return; // inventoryId not yet resolved - wait silently
    }

    const { silent = false } = options;
    const myRequestId = ++matchRequestIdRef.current;
    if (!silent) setShowNoResult(false);

    const mapMatchSort = (val) => {
      switch ((val || "").toLowerCase()) {
        case "newest":
          return "MOST_RECENT";
        case "oldest":
          return "OLDEST";
        case "bestmatch":
        default:
          return "MOST_RELEVANT";
      }
    };

    try {
      const request = {
        inventoryId: query.inventoryId,
        page: query.page,
        limit: query.limit,
        sortType: mapMatchSort(query.sortType),
        minScore: 40,
        userId: getUserId(),
      };

      const response = await getMatches(request);

      if (myRequestId !== matchRequestIdRef.current) return;

      const payload = _.get(response, "data.data", []);
      const totalCount = _.get(response, "data.totalCount", 0);

      setShowGitchaGeineLabel(true);
      setTotalCountMatch(totalCount);

      if (Array.isArray(payload) && payload.length > 0) {
        if (query.page === 1) {
          setAdListgitchaGenie([...payload]);
        } else {
          setAdListgitchaGenie(prev => [...prev, ...payload]);
        }
        setShowNoResult(false);
      } else if (query.page === 1) {
        setAdListgitchaGenie([]);
        setShowNoResult(true);
      }
    } catch (err) {
      if (myRequestId !== matchRequestIdRef.current) return;
      if (silent) return;
      toast.dismiss();
      toast.error(err?.message || "Failed to load matches");
      setAdListgitchaGenie([]);
      setShowNoResult(true);
      setTotalCountMatch(0);
    }
  };


  const updateFilter = (stateObject) => {
    sessionStorage.removeItem(FEED_CACHE_KEY);
    // Keep old results visible while the new query loads - prevents the
    // jumpy skeleton flash on every search/filter change. The fetch handler
    // replaces (not appends) when currPage === 1.
    setCurrPage(1);
    updateQuery(stateObject);
  };

  const updateFilterGitchaGenie = (stateObject) => {
    if (isGitchaGenieFilterRef.current) {
      // Keep old results visible until API responds (prevents flicker during slider drag)
      setCurrPage(1);
      updateQueryGitchaGenie(stateObject);
    }
  };

  const updateQuery = (stateObject) => {
    Object.keys(stateObject).forEach(function (key) {
      setFilterQuery((prevState) => ({
        ...prevState,
        [key]: stateObject[key],
      }));
    });
  };

  const updateQueryGitchaGenie = (stateObject) => {
    Object.keys(stateObject).forEach(function (key) {
      setFilterQueryGitchaGenie((prevState) => ({
        ...prevState,
        [key]: stateObject[key],
      }));
    });
  };

  const prevScrollPosition = usePrevious(scrollPosition);
  /**
   * To handle scroll
   */
  const handleScroll = () => {
    const el = myRef.current;
    const scrollHeight = el.scrollHeight;
    const scrollTop = el.scrollTop;
    const clientHeight = el.clientHeight;

    if (
      scrollHeight !== clientHeight &&
      scrollTop !== 0 &&
      Math.abs(scrollHeight - (scrollTop + clientHeight)) <= 1
    ) {
      setShowFooter(true);
      setScrollDirection("down");
    } else if (scrollTop < prevScrollPosition && scrollDirection !== "up") {
      setShowFooter(false);
      setScrollDirection("up");
    }
    setScrollPosition(scrollTop);
  };

  const handleShowMoreClick = () => {
    setShowFooter(false);
    setCurrPage((prevState) => prevState + 1);
  };

  // Restore window scroll position when returning from a want ad detail page.
  // useLayoutEffect fires BEFORE paint, preventing the 1-2 frame flash at scroll=0.
  useLayoutEffect(() => {
    const savedScroll = sessionStorage.getItem("feed_scrollTop");
    const list = isGitchaGenieFilter ? adListGitchaGenie : adList;
    if (savedScroll && list?.length > 0) {
      const scrollVal = parseInt(savedScroll, 10);
      // Immediate scroll before paint (via useLayoutEffect)
      window.scrollTo(0, scrollVal);
      // One deferred retry after images/layout settle
      const rafId = requestAnimationFrame(() => {
        window.scrollTo(0, scrollVal);
        sessionStorage.removeItem("feed_scrollTop");
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [adList, adListGitchaGenie, isGitchaGenieFilter]);

  /**
   * To handle the want-ad favorite/unfavorite
   */

  const handleFavoriteOrUnfavoriteWantAd = async (
    IsFavourite,
    Want_ad_Id,
    clientId = [],
  ) => {
    if (Want_ad_Id) {
      setReload(false);
      let data = {
        clientId: IsFavourite == false ? clientId : null,
        wantAdId: Want_ad_Id,
      };
      await (
        IsFavourite == false ? favouriteWantAd(data) : unFavouriteWantAd(data)
      )
        .then((response) => {
          if (response.errorCode == 0) {
            let message = "";
            if (clientId.length > 0) {
              message =
                "Want-Listing has been favorited for " +
                clientId.length +
                (clientId.length > 1 ? " clients" : " client");
            } else {
              message =
                IsFavourite == true
                  ? "Want-Listing has been removed from your favorites"
                  : "Want-Listing has been added to your favorites";
            }

            toast.success(message);
            setCurrPage(1);
            setAdList([]);
            setAdListgitchaGenie([]);
            if (isGitchaGenieFilter) {
              setReload(true);
            } else {
              updateQuery(initialQuery);
            }
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  //Remove existing all localStorage keys except userInfo.
  useEffect(() => {
    const keysToRemove = [
      WANT_AD_KEY,
      INVENTORY_KEY,
      STRENGTHEN_INVENTORY_KEY,
      STRENGTHEN_WANT_AD_KEY,
      CLIENT_DETAILS_KEY,
    ];
    // Never remove userLocation - it's needed for MLS-restricted tenants
    // to avoid a loading race on re-mount. The location is always re-written
    // by the userLocation effect anyway, so removing it only creates a gap.
    removeMultipleLocalStorageItems(keysToRemove);
  }, []);

  // Clear cache restoration flag after all mount effects complete.
  // 100ms delay ensures React 18's batched effects all see the flag before it clears,
  // preventing race conditions that could trigger unnecessary API calls/skeleton flashes.
  useEffect(() => {
    if (restoringFromCache.current) {
      const timer = setTimeout(() => {
        restoringFromCache.current = false;
        sessionStorage.removeItem(FEED_CACHE_KEY);
        if (
          isGitchaGenieFilterRef.current &&
          feedStateRef.current?.filterQueryGitchaGenie?.inventoryId
        ) {
          fetchWantAdsMatchingGitchaGenie(
            feedStateRef.current.filterQueryGitchaGenie,
            { silent: true }
          );
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  // Keep feedStateRef in sync so unmount cleanup can save without stale closures
  useEffect(() => {
    feedStateRef.current = {
      userLocation, adList, adListGitchaGenie, currPage,
      totalCount, totalCountMatch, filterQuery, filterQueryGitchaGenie,
      isGitchaGenieFilter, adType, sortParam, showGitchaGeineLabel,
      selectedProperty, timestamp: Date.now(),
    };
  });

  // Save feed cache on unmount so sidebar navigation preserves state
  useEffect(() => {
    return () => {
      const state = feedStateRef.current;
      if (state && (state.adList?.length > 0 || state.adListGitchaGenie?.length > 0)) {
        try {
          sessionStorage.setItem(FEED_CACHE_KEY, JSON.stringify(state));
        } catch (e) { /* sessionStorage full - continue without cache */ }
      }
    };
  }, []);

  /**
   *
   */
  useEffect(() => {
    getAgentsClientsList();
  }, [formData, reloadAgents, sessionData?.userInfo?.userId]);

  /**
   * To handle textbox value change on agent client search
   * @param {any} event
   */
  const onChangeText = (event) => {
    let value = "";
    if (event) {
      value = handleTextBoxChange(event);
    }

    setFormData((formData) => value);
  };

  /**
   * To get agents clients list
   * @param {*} query
   */
  const getAgentsClientsList = async (query) => {
    let a = {
      search: formData,
      sortBy: "createdOn",
      sortDir: "desc",
    };
    await getAgentsList(a)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult) {
          setResults(responseResult);
        } else {
          console.log("Something went wrong");
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const handleWantAdItemClick = (wantAdId, slug) => {
    // Save window scroll position (page scrolls via viewport, not the listing div)
    sessionStorage.setItem("feed_scrollTop", String(window.scrollY));

    // Save full feed state for back-button restoration
    try {
      sessionStorage.setItem(FEED_CACHE_KEY, JSON.stringify({
        userLocation, adList, adListGitchaGenie, currPage,
        totalCount, totalCountMatch, filterQuery, filterQueryGitchaGenie,
        isGitchaGenieFilter, adType, sortParam, showGitchaGeineLabel,
        selectedProperty, timestamp: Date.now(),
      }));
    } catch (e) {
      // sessionStorage full or stringify failed - continue without cache
    }

    const sessionUserInfo = getLocalStorage(USER_INFO_KEY);
    if (sessionUserInfo?.userInfo?.userId) {
      setLocalStorage(WANT_AD_KEY, {
        wantAdId: wantAdId,
        isFromHomeFeed: true,
      });
      navigate(`${pageURLs.wantAdDetails}/${slug}`);
    } else {
      navigate(`${pageURLs.wantAdDetails}/${slug}`);
      // setLocalStorage(WANT_AD_ARRAY_KEY, wantAdArray);
    }
  };

  const handleGitchGenieFilter = (value) => {
    sessionStorage.removeItem("feed_scrollTop");
    sessionStorage.removeItem(FEED_CACHE_KEY);
    isGitchaGenieFilterRef.current = value;
    setIsGitchaGenieFilter(value);
    // Auto-switch sort to "Best Match" when entering Match tab
    if (value) {
      setSortParam({ value: "bestMatch", label: "Best Match" });
    } else {
      setSortParam({ value: "newest", label: "Newest" });
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const handleSortParam = (selectedParam) => {
    sessionStorage.removeItem("feed_scrollTop");
    sessionStorage.removeItem(FEED_CACHE_KEY);
    setSortParam(selectedParam);
  };
  const getLocationText = () => {
    if (isGitchaGenieFilter) {
      const count = filteredMatchCount;
      if (count != null && count > 0) {
        const propAddr = selectedProperty?.Location || selectedProperty?.City || "";
        return `${count} Buyer Match${count !== 1 ? "es" : ""} for ${propAddr}`;
      }
      return "";
    } else {
      return totalCount != null
        ? `Real Time Home ${adType == BUY
          ? " Buyers"
          : "Renters"
        }   In/Near ${_.get(userLocation, "formatted_address", "")}`
        : "";
    }
  };

  const ref = useRef(null);

  const handleOnMouseEnter = () => {
    setMatchTutorial(true);
  };
  const handleToggle = async () => {
    let tutorialDisplayedPages = getLocalStorage("tutorialDisplayedPages");
    tutorialDisplayedPages = tutorialDisplayedPages
      ? tutorialDisplayedPages
      : [];
    tutorialDisplayedPages.push(1); //
    setLocalStorage("tutorialDisplayedPages", tutorialDisplayedPages);
    setNotificationIcon(false);
    if (sessionData && sessionData?.userInfo?.userId) {
      await saveTutorialStatus(1, 1); //Save status
    }
  };
  const [showWelcomeModal, setShowWelcomeModal] = useState();

  const handleCloseModal = () => {
    setShowWelcomeModal(false);
  };

  useEffect(() => {
    if (
      showNotification === true &&
      sessionData &&
      sessionData?.userInfo?.userId
    ) {
      checkUserInventoryCount();
    }
  }, []);

  const checkUserInventoryCount = async () => {
    let inventoryCount = await getUserInventoryCount();
    const responseResult = _.get(inventoryCount, "result", null);
    setNotificationIcon(false);
    if (inventoryCount.errorCode == 0 && responseResult) {
      if (responseResult[0].count > 0) {
        setNotificationIcon(true);
      }
    }
  };
  /**
   * To check gitcha is available in searched location
   * @param {*} city
   * @param {*} state
   * @param {*} county
   * @param {*} formatted_address
   * @param {*} locationObject
   */
  const checkGitchaAvailableInLoc = async (
    city,
    state,
    county,
    formatted_address,
    locationObject,
  ) => {
    // Tenant-restricted sites skip the availability check entirely -
    // location search is already constrained to allowed cities
    if (!isGitchaDefaultWebsite) {
      locationObject["isFromAvailableCities"] = true;
      setLocalStorage("userLocation", locationObject);
      setUserLocation(locationObject);
      setGitchaNotAvailable(false);
      return;
    }

    try {
      const reqPayLoad = {
        county: county,
        state: state,
        city: city,
      };
      await checkGitchaAvailable(reqPayLoad).then((response) => {
        if (_.get(response, "result") == true) {
          locationObject["isFromAvailableCities"] = true;
          setLocalStorage("userLocation", locationObject);
          setUserLocation(locationObject);
          setGitchaNotAvailable(false);
        } else {
          // City not available - show MLS popup then available cities
          setLocalStorage("userLocation", locationObject);
          setUserLocation(locationObject);
          setGitchaNotAvailable(true);
          setShowGitchaNotAvailablePopup(true);
        }
      });
    } catch (error) {
      setLocalStorage("userLocation", locationObject);
      setUserLocation(locationObject);
      setGitchaNotAvailable(true);
      setShowGitchaNotAvailablePopup(true);
    }
  };

  const selectedClient = getLocalStorage("MatchSelectedClient");
  const currentSelectedProperty = getLocalStorage("selectedPropertyId");
  const isPremiumUser = getLocalStorage("isPremiumUser");
  /**
   * To handle rendering result
   * @returns
   */
  const renderResult = () => {
    if (isGitchaGenieFilter) {
      if (
        showNoResult &&
        adListGitchaGenie &&
        adListGitchaGenie.length === 0 &&
        currentSelectedProperty?.Inv_Id == null
      ) {
        // Match tab - no property selected: skeleton cards with gradient fade
        return (
          <div className="match-skeleton-overlay">
            <div className="match-skeleton-overlay__list">
              {[0, 1, 2, 3].map((i) => (
                <div
                  className="match-skeleton-card"
                  key={i}
                  style={{ animationDelay: `${i * 0.15}s` }}
                >
                  <div className="match-skeleton-card__img" />
                  <div className="match-skeleton-card__lines">
                    <div className="match-skeleton-card__line match-skeleton-card__line--short" />
                    <div className="match-skeleton-card__line match-skeleton-card__line--long" />
                    <div className="match-skeleton-card__line match-skeleton-card__line--medium" />
                    <div className="match-skeleton-card__line match-skeleton-card__line--short" />
                  </div>
                </div>
              ))}
            </div>
            <div className="match-skeleton-overlay__fade" />
          </div>
        );
      } else if (
        showNoResult &&
        adListGitchaGenie &&
        adListGitchaGenie.length === 0 &&
        currentSelectedProperty?.Inv_Id
      ) {
        // Match tab - property selected but no matches found
        return (
          <div className="match-no-result">
            <div className="match-no-result__icon-wrap">
              <img src={BellIcon} alt="" />
              <span className="match-no-result__pulse" />
            </div>
            <h3 className="match-no-result__heading">
              No matches yet
            </h3>
            <p className="match-no-result__body">
              We’ll notify you when buyers post Want-Listings that match this property. More buyers are posting daily.
            </p>
          </div>
        );
      } else if (
        !showNoResult &&
        adListGitchaGenie &&
        adListGitchaGenie.length === 0
      ) {
        return (
          <>
            <div
              className={
                !showFooter
                  ? "property-listing-outer "
                  : "property-listing-outer"
              }
              onScroll={handleScroll}
              ref={myRef}
            >
              <HomeLoadingSkeleton />
            </div>
          </>
        );
      } else {
        return (
          <div
            className={
              !showFooter ? "property-listing-outer" : "property-listing-outer"
            }
            onScroll={handleScroll}
            ref={myRef}
          >
            {filteredMatchList &&
              filteredMatchList.map((wa, index) => (
                <CardComponent
                  list={wa}
                  key={index}
                  handleFavoriteOrUnfavoriteWantAd={
                    handleFavoriteOrUnfavoriteWantAd
                  }
                  results={results}
                  clientSearchText={formData}
                  onChangeText={onChangeText}
                  onAddClient={() => setReloadAgents(!reloadAgents)}
                  handleWantAdItemClick={handleWantAdItemClick}
                />
              ))}
            {showGitchaGeineLabel &&
              wantAdCountGitchaGenie < totalCountMatch && (
                <div className="text-center pt-3 pb-3 w-100">
                  <button
                    className="btn btn-primary"
                    onClick={() => handleShowMoreClick()}
                  >
                    Show More
                  </button>
                </div>
              )}
          </div>
        );
      }
    } else {
      if (!userLocation?.isFromAvailableCities) {
        // [US-002] For restricted tenants, show loading skeleton while defaultMarket resolves
        // instead of the "Explore Available Markets" page
        if (mlsRestricted) {
          return (
            <div className={!showFooter ? "property-listing-outer " : "property-listing-outer"}
              onScroll={handleScroll} ref={myRef}>
              <HomeLoadingSkeleton />
            </div>
          );
        }
        return (
          <>
            <div className="no-result-wrap">
              <GitchaAvailableLocations
                setUserLocation={setUserLocation}
                userLocation={userLocation}
              />
            </div>
          </>
        );
      // US-057: Warmer empty state with icon and CTA
      } else if (showNoResult && adList && adList.length === 0) {
        return (
          <div className="empty-state">
            <div className="empty-state__icon">
              <Search size={28} />
            </div>
            <div className="empty-state__title">
              No Want-Listings in {userLocation?.city || "this area"} yet
            </div>
            <div className="empty-state__text">
              {mlsRestricted
                ? "No Want-Listings found yet. Check back soon."
                : "Try broadening your search or explore one of our available markets."}
            </div>
            {!mlsRestricted && (
              <button
                className="btn btn-outline-primary"
                onClick={() => {
                  setUserLocation(null);
                  setAdList([]);
                  setShowNoResult(false);
                }}
              >
                View Available Markets
              </button>
            )}
          </div>
        );
      } else if (!showNoResult && adList && adList.length === 0) {
        return (
          <>
            <div
              className={
                !showFooter
                  ? "property-listing-outer "
                  : "property-listing-outer"
              }
              onScroll={handleScroll}
              ref={myRef}
            >
              <HomeLoadingSkeleton />
            </div>
          </>
        );
      } else {
        return (
          <div
            className={
              !showFooter ? "property-listing-outer" : "property-listing-outer"
            }
            onScroll={handleScroll}
            ref={myRef}
          >
            {adList &&
              adList.map((wa, index) => (
                <CardComponent
                  list={wa}
                  key={index}
                  handleFavoriteOrUnfavoriteWantAd={
                    handleFavoriteOrUnfavoriteWantAd
                  }
                  results={results}
                  clientSearchText={formData}
                  onChangeText={onChangeText}
                  onAddClient={() => setReloadAgents(!reloadAgents)}
                  handleWantAdItemClick={handleWantAdItemClick}
                />
              ))}
            {wantAdCount < totalCount && (
              <div className="text-center pt-3 pb-3 w-100">
                <button
                  className="btn btn-primary"
                  onClick={() => handleShowMoreClick()}
                >
                  Show More
                </button>
              </div>
            )}
          </div>
        );
      }
    }
  };

  // Client-side price filtering for Match tab slider.
  // Shows all buyers whose budget range includes the slider value (with tolerance).
  const filteredMatchList = useMemo(() => {
    if (!adListGitchaGenie || adListGitchaGenie.length === 0) return adListGitchaGenie;
    if (matchPriceFilter == null) return adListGitchaGenie;
    const tolerance = adType === BUY ? 25000 : adType === LONG_RENT ? 250 : 50;
    return adListGitchaGenie.filter((ad) => {
      const mn = ad.Budget_mn || 0;
      const mx = ad.Budget_mx || mn;
      // Buyer's range overlaps: slider value falls within [mn - tolerance, mx + tolerance]
      return matchPriceFilter >= (mn - tolerance) && matchPriceFilter <= (mx + tolerance);
    });
  }, [adListGitchaGenie, matchPriceFilter, adType]);

  const filteredMatchCount = matchPriceFilter != null
    ? filteredMatchList?.length || 0
    : totalCountMatch;

  const hasFeedListings = isGitchaGenieFilter
    ? (totalCountMatch != null && totalCountMatch > 0)
    : adList && adList.length > 0;

  return (
    <>
    {/* Fixed overlays rendered OUTSIDE PageTransition so the page-settle
        transform doesn't break position:fixed positioning and backdrop blur */}
    <DemoTransition
      show={showDemoTransition}
      onComplete={handleDemoTransitionComplete}
      tenantName="Gitcha"
      logoSrc={appConfig?.tenant?.assets?.logos?.logoWhite}
    />
    {mlsRestricted && user && !appConfig && (
      <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#060d1a" }} />
    )}
    {mlsRestricted && (!user || loginPending) && (
      <TenantLoginPage onLoginSuccess={handleTenantLoginSuccess} />
    )}
    {showTourLaunch && !enterpriseTour.isActive && (
      <TourLaunchDialog
        onStart={() => {
          setShowTourLaunch(false);
          enterpriseTour.actions.startTour();
        }}
        onSkip={() => {
          setShowTourLaunch(false);
          localStorage.setItem(TOUR_COMPLETED_KEY, "skipped");
        }}
      />
    )}
    <PageTransition>
      {sessionData ? (
        <PreLaunchCityRequestModal
          show={isCityInPreLaunch}
          onHide={() => {
            setIsCityInPreLaunch(false);
            setPreLaunchStateName(null);
          }}
          setUserLocation={setUserLocation}
          userLocation={userLocation}
          PreLaunchStateName={PreLaunchStateName}
        />
      ) : (
        <></>
      )}
      {showWelcomeModal && (
        <WelcomeModal
          name={getLocalStorage("userInfo")?.userInfo?.firstName}
          videoUrl={
            "https://gitcha-uploads.s3.us-east-1.amazonaws.com/public/First+Steps_+Quick+5+Min+Walkthrough+of+Gitcha.mp4"
          }
          showModal={showWelcomeModal}
          onClose={handleCloseModal}
        />
      )}
      <MatchTutorial
        show={showMatchTutorial}
        onHide={() => {
          handleToggle();
          setMatchTutorial(false);
        }}
      />
      <RequestStayAvailability
        show={showRequestStayPopuop}
        locationFromSTorage={browserLocation}
        onHide={handleStayRequestPopupClose}
      />
      {/* Skip browser geolocation for MLS tenants - they use defaultMarket from tenant config */}
      {!mlsRestricted && (
        <GeoLocation handleUserLocation={handleUserLocation} page={HOME_PAGE} />
      )}
      {/* JAKE-FILTER-IMPROVEMENTS: Added adList and adListGitchaGenie for price histogram */}
      <filterContext.Provider
        value={{
          adType: adType,
          clearFilterQuery,
          adList,
          adListGitchaGenie,
          totalCountMatch,
          filteredMatchList,
          filteredMatchCount,
          setMatchPriceFilter,
        }}
      >

        {/* BLS tenant sign-in gate and main content */}
        {mlsRestricted && (!user || loginPending) ? (
          <></> // Login page rendered above, outside PageTransition
        ) : (
        <>
        {/* JAKE-FUNNEL: Aurora background only on Match tab when no property selected */}
        <PageWrapper
          className={isGitchaGenieFilter && !currentSelectedProperty?.Inv_Id ? 'outer-wrap--aurora' : ''}
          headerSidebarProps={{
            onLogoDoubleClick: () => {
              removeLocalStorage("userLocation");
              setUserLocation(null);
              setForceShowSplash(true);
            },
          }}
          headerProps={{
            onLogoDoubleClick: () => {
              removeLocalStorage("userLocation");
              setUserLocation(null);
              setForceShowSplash(true);
            },
          }}
        >
          {/* Transition overlay - renders above both hero and feed */}
          {showTransition && (
            <SearchTransition
              show={showTransition}
              cityName={transitionCity}
              onComplete={() => setShowTransition(false)}
            />
          )}
          {(forceShowSplash || (!user && userLocation === null)) && !isGitchaGenieFilter ? (
            <HeroSplash
              setUserLocation={setUserLocation}
              adType={adType}
              mlsRestrictedCities={mlsRestricted ? availableCities : null}
              onSearchStart={(locationObj, city, state, county, formatted_address) => {
                setForceShowSplash(false);
                setTransitionCity(locationObj.formatted_address);
                setShowTransition(true);
                // Delay the location check so the transition overlay renders first
                setTimeout(() => {
                  checkGitchaAvailableInLoc(city, state, county, formatted_address, locationObj);
                }, 100);
              }}
            />
          ) : (
            <div className="d-flex flex-row">
              <Offcanvas
                show={offCanvaShow}
                onHide={handleCanvasClose}
                placement="start"
                className="pushmenu"
              >
                <Offcanvas.Header closeButton>
                  <h2>Filter</h2>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <div className="left-panel">
                    <LeftpanelTab
                      updateFilter={updateFilter}
                      filterQuery={filterQuery}
                      handleGitchGenieFilter={handleGitchGenieFilter}
                      clearFilterGitchaGenie={clearFilterGitchaGenie}
                      updateFilterGitchaGenie={updateFilterGitchaGenie}
                      selectedProperty={selectedProperty}
                      setSelectedProperty={setSelectedProperty}
                      setUserLocation={setUserLocation}
                      userLocation={userLocation}
                      gitchaNotAvailble={gitchaNotAvailble}
                      checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
                      mlsRestrictedCities={mlsRestricted ? availableCities : null}
                    />
                  </div>
                </Offcanvas.Body>
              </Offcanvas>
              <div className="pe-md-3 pe-lg-3 pe-xl-4 d-none d-md-flex home-left-pannel-wrapper">
                <div className="left-panel position-relative">
                  {showNotificationIcon && (
                    <div className="ring-container">
                      <div className="circlebtn">
                        <div className="ringring"></div>
                      </div>
                    </div>
                  )}

                  <div ref={ref} className="w-100 position-relative">
                    {showNotificationIcon && (
                      <button
                        className="btn red-hotspot-btn position-absolute"
                        onClick={() => setMatchTutorial(true)}
                        onMouseEnter={handleOnMouseEnter}
                      ></button>
                    )}
                  </div>

                  <LeftpanelTab
                    updateFilter={updateFilter}
                    filterQuery={filterQuery}
                    handleGitchGenieFilter={handleGitchGenieFilter}
                    clearFilterGitchaGenie={clearFilterGitchaGenie}
                    updateFilterGitchaGenie={updateFilterGitchaGenie}
                    selectedProperty={selectedProperty}
                    setSelectedProperty={setSelectedProperty}
                    setUserLocation={setUserLocation}
                    userLocation={userLocation}
                    gitchaNotAvailble={gitchaNotAvailble}
                    checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
                    mlsRestrictedCities={mlsRestricted ? availableCities : null}
                  />
                </div>
              </div>
              <div className="ps-md-3 ps-lg-3 ps-xl-4 home-listing-wrapper" data-tour="feed-main">
                <div className="right-panel">
                  {hasFeedListings &&
                    <div className="top-sec mb-24 md-mt-24">
                      <div className="d-flex">
                        <div className="col">
                          {getTitleText(
                            userLocation,
                            isGitchaGenieFilter,
                            isCityInPreLaunch,
                            getLocationText,
                          )}
                        </div>
                        {/* Filter trigger - visible only at phone widths */}
                        <button
                          className="feed-filter-btn d-md-none"
                          onClick={handleCanvasShow}
                          aria-label="Open filters"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="4" y1="6" x2="20" y2="6" />
                            <line x1="8" y1="12" x2="20" y2="12" />
                            <line x1="12" y1="18" x2="20" y2="18" />
                          </svg>
                        </button>
                        <div
                          // col="12"
                          className="d-flex flex-column"
                          style={{ width: "auto !important" }}
                        >
                          <div className="d-flex select-wrap position-relative gap-3">
                            <Form.Label style={{ fontSize: 15 }}>
                              Sort By:
                            </Form.Label>
                            <SelectDropDown
                              id="multi3"
                              name="colors3"
                              options={options}
                              className="basic-single sort-select d-inline-block mb-0"
                              classNamePrefix="select"
                              placeholder="Select"
                              value={sortParam}
                              onChange={handleSortParam}
                              styles={{
                                dropdownIndicator: (provided, state) => ({
                                  ...provided,
                                  transform:
                                    state.selectProps.menuIsOpen &&
                                    "rotate(180deg)",
                                }),
                              }}
                              errorClass="d-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                  {renderResult()}
                </div>
              </div>
            </div>
          )}
          <SiteFooter show={showFooter} />
        </PageWrapper>
        </>
        )}

      </filterContext.Provider>

      <AgentLiketoStartModal />
      <GitchaNotAvailableModal
        show={showGitchaNotAvailablePopup}
        onHide={() => setShowGitchaNotAvailablePopup(false)}
      />
    </PageTransition>
    </>
  );
};

export default Home;